import type { DropdownFilterCallback } from '../../../components/Dropdown/Dropdown';
import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { UseAppForm } from '../../form';
import type { FieldPermissionsRule } from '../FieldPermissions/FieldPermisssions';
import type { PermissionFieldProps } from '../PermissionField/PermissionField';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_ARRAY } from '../../../lib/globals';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { useFieldContext, useFormContext } from '../../form';

interface PermissionAppFieldProps extends PermissionFieldProps {
  /**
   * Index of the permission in the list
   */
  index: number;
}

const PermissionAppField = ({ index, ...props }: PermissionAppFieldProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext();

  const onRemove = useCallback(() => {
    field.removeValue(index);
  }, [field, index]);

  return (
    <form.AppField name={`${field.name}[${index}]`}>
      {(subField) => (
        <subField.PermissionField {...props} onRemove={onRemove} />
      )}
    </form.AppField>
  );
};

interface PermissionsFieldProps {
  /**
   * Permissions rules columns configuration.
   */
  ruleColumn?: FieldPermissionsRule[];
  /**
   * Content types data
   */
  ctdsOptions?: DropdownOption[];
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Permissions type
   */
  type?: 'CTD' | 'CO' | '';
  /**
   * Content type definition dropdown placeholder
   */
  ctdPlaceholder?: string;
  /**
   * Callback for filtering content types in dropdown
   */
  ctdsFilterCallback?: DropdownFilterCallback;
  /**
   * If field should show add button
   */
  hasAddButton?: boolean;
  /**
   * Test id for field
   */
  testId?: string;
}

type Permission = {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  contentTypeDefinition: { id: string | null };
  type: 'CTD' | 'CO' | '';
};

const PermissionsField = ({
  ruleColumn = EMPTY_ARRAY,
  ctdsOptions = EMPTY_ARRAY,
  disabled = false,
  type = '',
  ctdPlaceholder = '',
  ctdsFilterCallback,
  hasAddButton = true,
  testId = '',
}: PermissionsFieldProps) => {
  const { t } = useTranslation();
  const field = useFieldContext<Permission[]>();

  const permissions = useMemo(() => field.state.value, [field.state.value]);

  const onAdd = useCallback(
    () =>
      field.pushValue({
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false,
        contentTypeDefinition: { id: null },
        type,
      }),
    [field, type],
  );

  return (
    <>
      <div className="w-full hidden sm:grid grid-cols-2 items-center">
        {permissions.length > 0 && (
          <div className="grid col-start-2 grid-cols-4 w-full justify-items-center dark:text-slate-300">
            {ruleColumn.map((rule) => (
              <span key={rule.name} className="uppercase text-sm">
                {rule.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col sm:grid sm:grid-cols-2 items-center gap-y-4">
        {permissions.map((_, index) => {
          return (
            <PermissionAppField
              key={index}
              index={index}
              ctdsOptions={ctdsOptions}
              ruleColumn={ruleColumn}
              ctdPlaceholder={ctdPlaceholder}
              disabled={disabled}
              type={type}
              ctdsFilterCallback={ctdsFilterCallback}
              testId={testId}
            />
          );
        })}
      </div>

      {hasAddButton && (
        <div className="flex flex-col w-full mt-6">
          <Button
            onClick={onAdd}
            disabled={disabled}
            {...getTestProps(testId, 'add', 'testId')}
          >
            {t('ApiKeys.AddRule')}
          </Button>
        </div>
      )}
    </>
  );
};

export default PermissionsField;
